<template>
  <div>
    <vs-popup
      :title="$t(`health_services.${selectedSubject}.${selectedService}`)"
      :active.sync="activePrompt"
    >
      <div class="vx-col">
        <label class="vs-input--label">{{ $t('admin.service_subject.unit') }}</label>
        <v-select
          v-model="selectedUnit"
          :clearable="false"
          :options="unitOptions"
          :reduce="option => option.value"
          name="unit"
          v-validate="'required'"
          :data-vv-as="$t('admin.service_subject.unit')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
          class="w-full"
        />
        <span class="text-danger text-sm" v-show="errors.has('unit')">
          {{ errors.first('unit') }}
        </span>
      </div>
      <div class="vx-col">
        <label class="vs-input--label">{{ $t('admin.service_subject.price') }}</label>
        <vs-input
          name="price"
          type="number"
          v-validate="'required'"
          :data-vv-as="$t('admin.service_subject.price')"
          class="w-full"
          v-model="selectedPrice"
          @keyup="selectedPrice = translateNumbers(selectedPrice)"
        />
        <span class="text-danger text-sm" v-show="errors.has('price')">
          {{ errors.first('price') }}
        </span>
      </div>
      <div class="vx-col">
        <label class="vs-input--label">{{ $t('admin.service_subject.percent') }}</label>
        <vs-input
          name="percent"
          type="number"
          v-validate="'required|percent'"
          :data-vv-as="$t('admin.service_subject.percent')"
          class="w-full"
          v-model="selectedPercent"
          @keyup="selectedPercent = translateNumbers(selectedPercent)"
        />
        <span class="text-danger text-sm" v-show="errors.has('percent')">
          {{ errors.first('percent') }}
        </span>
      </div>
      <vs-row vs-justify="flex-end" class="mt-base">
        <vs-button class="mr-2" @click="activePrompt = false" color="danger" type="border">
          {{ $t('forms.cancel') }}
        </vs-button>
        <vs-button
          id="edit_btn"
          color="success"
          class="vs-con-loading__container mr-2"
          @click="editPrice"
          :disabled="disabled_btn"
        >
          {{ $t('forms.edit') }}
        </vs-button>
      </vs-row>
    </vs-popup>
    <vx-card v-for="(category, key) in categorySubjects" :key="key" class="mb-base">
      <h2 class="mb-3">{{ $t(`places.categories.${key}`) }}</h2>
      <vs-collapse type="shadow">
        <vs-collapse-item v-for="(subject, index) in category" :key="index">
          <!-- Header -->
          <div slot="header">
            <span class="leading-none font-medium">{{ $t(`places.subjects.${subject}`) }}</span>
          </div>
          <vs-table :data="price_config[subject]" class="mb-base">
            <template slot="thead">
              <vs-th sort-key="service">{{ $t('admin.service_subject.title') }}</vs-th>
              <vs-th sort-key="unit">{{ $t('admin.service_subject.unit') }}</vs-th>
              <vs-th sort-key="percent">{{ $t('admin.service_subject.percent') }}</vs-th>
              <vs-th sort-key="price">{{ $t('admin.service_subject.price') }}</vs-th>
              <vs-th>{{ $t('forms.actions') }}</vs-th>
            </template>

            <template slot-scope="{ data }">
              <tbody v-if="data.length">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>{{ $t(`health_services.${subject}.${tr.service}`) }}</vs-td>
                  <vs-td>{{ $t(`units.${tr.unit}`) }}</vs-td>
                  <vs-td>{{ tr.percent | persian_digits }}</vs-td>
                  <vs-td>{{ tr.price | toman }}</vs-td>
                  <vs-td>
                    <feather-icon
                      icon="EditIcon"
                      svgClasses="w-5 h-5 hover:text-success stroke-current"
                      class="ml-2"
                      @click="openEditPrompt(subject, tr)"
                    />
                  </vs-td>
                </vs-tr>
              </tbody>
            </template>
          </vs-table>
        </vs-collapse-item>
      </vs-collapse>
    </vx-card>
  </div>
</template>

<script>
import { TranslateNumbers } from '@/utils'

export default {
  name: 'ServicesPricingForm',
  props: {
    province: { type: String, required: true }
  },
  data() {
    return {
      serviceDetailConfigData: {},
      serviceDetails: {},
      categorySubjects: {},
      activePrompt: false,
      disabled_btn: false,
      selectedPrice: '',
      selectedPercent: '',
      selectedUnit: '',
      selectedSubject: '',
      selectedService: ''
    }
  },
  computed: {
    price_config() {
      const price_config = {}
      for (const [key, value] of Object.entries(this.serviceDetails)) {
        const newValue = []
        for (const [service, v] of Object.entries(value)) {
          newValue.push({ service, price: v.price, unit: v.unit, percent: v.percent })
        }
        price_config[key] = newValue
      }
      return price_config
    },
    unitOptions() {
      return this.$store.getters['enum/selectOptions']('InvoiceUnit', 'units')
    }
  },
  methods: {
    openEditPrompt(subject, tr) {
      this.activePrompt = true
      this.selectedPrice = tr.price
      this.selectedPercent = tr.percent
      this.selectedUnit = tr.unit
      this.selectedSubject = subject
      this.selectedService = tr.service
    },
    editPrice() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.disabled_btn = true
          this.$vs.loading({
            background: 'success',
            container: '#edit_btn',
            color: '#fff',
            scale: 0.45
          })
          this.serviceDetails[this.selectedSubject][this.selectedService].price = this.selectedPrice
          this.serviceDetails[this.selectedSubject][this.selectedService].percent = this.selectedPercent
          this.serviceDetails[this.selectedSubject][this.selectedService].unit = this.selectedUnit
          this.$store
            .dispatch('config/editConfig', {
              id: this.serviceDetailConfigData.id,
              data: this.serviceDetailConfigData
            })
            .then(() => {
              this.$vs.notify({
                title: this.$t('forms.successfully_edited'),
                iconPack: 'feather',
                icon: 'icon-alert-circle',
                color: 'success'
              })
              this.$vs.loading.close('#edit_btn > .con-vs-loading')
              this.disabled_btn = false
              this.activePrompt = false
            })
            .finally(() => this.$vs.loading.close())
        }
      })
    },
    translateNumbers(val) {
      return TranslateNumbers(val)
    }
  },
  created() {
    this.$vs.loading()
    this.$store.commit('config/SET_CONFIGS_QUERY', {
      key: 'Place:Subject:Service:Detail',
      groups: `province:${this.province}`
    })
    this.$store.dispatch('config/fetchConfigs').then(res => {
      this.serviceDetailConfigData = Object.assign({}, res.data[0])
      this.serviceDetails = this.serviceDetailConfigData.value
    })
    this.$store
      .dispatch('config/fetchConfig', 'Place:Category:Subject')
      .then(res => {
        this.categorySubjects = Object.assign({}, res.data.value)
        this.categorySubjects['common'] = ['common']
      })
      .finally(() => this.$vs.loading.close())
  }
}
</script>

<style scoped></style>
